import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { FormGroup } from '@/components/FormGroup';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { useAuth } from '@/contexts/AuthContext';
import { useUpdateShopifyCredentials } from '@/hooks/useUpdateShopifyCredentials';
import i18n from '@/lib/i18n';

const formSchema = z.object({
  url: z
    .string()
    .min(1, {
      message: i18n.t(
        'pages.Settings.components.Shopify.components.CredentialsForm.validation.urlRequired',
      ),
    })
    .url(
      i18n.t(
        'pages.Settings.components.Shopify.components.CredentialsForm.validation.urlInvalid',
      ),
    ),
  shopifyAccessToken: z.string().min(1, {
    message: i18n.t(
      'pages.Settings.components.Shopify.components.CredentialsForm.validation.accessTokenRequired',
    ),
  }),
});

type FormData = z.infer<typeof formSchema>;

export function CredentialsForm() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Settings.components.Shopify.components.CredentialsForm',
  });
  const { user } = useAuth();

  const [isEditable, setIsEditable] = useState<boolean>(() => {
    return !(user?.customer.shopifyUrl && user?.customer.shopifyAccessToken);
  });

  const { updateShopifyCredentials } = useUpdateShopifyCredentials();

  const {
    formState: { isSubmitting, errors },
    register,
    handleSubmit,
    setValue,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      url: user?.customer.shopifyUrl ?? '',
      shopifyAccessToken: user?.customer.shopifyAccessToken ?? '',
    },
  });

  function handleEdit() {
    setValue('shopifyAccessToken', '');
    setIsEditable(true);
  }

  async function onSubmit({ url, shopifyAccessToken }: FormData) {
    try {
      const response = await updateShopifyCredentials({
        shopifyUrl: url,
        shopifyAccessToken: shopifyAccessToken,
      });

      if (response.success) {
        toast.success(response.message);
        setIsEditable(false);
      }
    } catch (error) {
      let message = t('toast.error');

      if (error instanceof AxiosError) {
        message = error.response?.data?.message ?? message;
      }

      toast.error(message);
    }
  }

  return (
    <form className="space-y-6" noValidate onSubmit={handleSubmit(onSubmit)}>
      <h3 className="text-lg font-bold">{t('title')}</h3>

      <FormGroup
        className="space-y-2"
        error={{ message: errors.url?.message, font: 'sm' }}
      >
        <Label htmlFor="url">{t('urlLabel')}</Label>

        <Input
          id="url"
          placeholder={t('urlPlaceholder')}
          className="w-full"
          disabled={!isEditable}
          {...register('url')}
        />

        {!errors.url?.message && (
          <p
            className="text-sm text-muted-foreground"
            dangerouslySetInnerHTML={{
              __html: `${t('urlError')}`,
            }}
          />
        )}
      </FormGroup>

      <FormGroup
        className="space-y-2"
        error={{ message: errors.shopifyAccessToken?.message, font: 'sm' }}
      >
        <Label htmlFor="shopifyToken">{t('accessTokenLabel')}</Label>

        <Input
          id="shopifyIdToken"
          type="password"
          placeholder={t('accessTokenPlaceholder')}
          className="w-full"
          disabled={!isEditable}
          {...register('shopifyAccessToken')}
        />

        {!errors.shopifyAccessToken?.message && (
          <p className="text-sm text-muted-foreground">
            {t('accessTokenError')}
          </p>
        )}
      </FormGroup>

      {!isEditable && (
        <Button
          type="button"
          className="w-full bg-black text-white transition-colors hover:bg-gray-800"
          onClick={handleEdit}
        >
          {t('editButton')}
        </Button>
      )}

      {isEditable && (
        <Button
          type="submit"
          className="mt-auto w-full bg-black text-white transition-colors hover:bg-gray-800"
          disabled={isSubmitting}
        >
          {isSubmitting ? t('savingButton') : t('saveButton')}
        </Button>
      )}
    </form>
  );
}
